
import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, User as FirebaseUser } from 'firebase/auth';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import UserLayout from './components/layout/UserLayout';
import { User, UserRole, Tenant } from './types';
import { auth } from './services/firebase';
import { firestoreService } from './services/firestoreService';
import Spinner from './components/common/Spinner';
import { AuthContext } from './contexts/AuthContext';

const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      setLoading(true);
      if (firebaseUser) {
        try {
          const userProfile = await firestoreService.getUserProfile(firebaseUser.uid);
          
          if (!userProfile) {
            setAuthError("No se encontró el perfil de usuario.");
            await signOut(auth);
            setLoading(false);
            return;
          }

          if (!userProfile.active) {
            setAuthError("Tu cuenta de usuario ha sido desactivada.");
            await signOut(auth);
            setLoading(false);
            return;
          }

          if (userProfile.tenantId) {
            const tenantData = await firestoreService.getTenant(userProfile.tenantId);
            const isSuperAdmin = userProfile.role === UserRole.PLATFORM_OWNER;
            
            if (tenantData) {
              if (!tenantData.active && !isSuperAdmin) {
                setAuthError("El acceso para esta agencia ha sido suspendido. Contacta con el administrador de la plataforma.");
                await signOut(auth);
                setTenant(null);
                setUser(null);
                setLoading(false);
                return;
              }

              setTenant(tenantData);
              if (tenantData.settings.branding.primaryColor) {
                document.documentElement.style.setProperty('--brand-primary', tenantData.settings.branding.primaryColor);
              }
              if (!isSuperAdmin) {
                 document.title = `${tenantData.name} | MysteryShopperIA`;
              }
            }
          }
          
          setUser(userProfile);
          setAuthError(null);
        } catch (e) {
          console.error("Auth Error:", e);
          setAuthError("Error al validar tus credenciales.");
        }
      } else {
        setUser(null);
        setTenant(null);
        document.title = "MysteryShopperIA";
        document.documentElement.style.setProperty('--brand-primary', '#3B82F6');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setAuthError(null);
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const authContextValue = useMemo(() => ({ user, tenant, loading, login, logout, authError }), [user, tenant, loading, login, logout, authError]);
  
  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

const AppContent: React.FC = () => {
  const authContext = useContext(AuthContext);

  if (authContext?.loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black">
        <Spinner size="lg" />
      </div>
    );
  }

  const user = authContext?.user;
  if (!user) return <LoginPage />;
  
  return (
    <HashRouter>
      <Routes>
        {user.role === UserRole.CLIENT_VIEWER ? (
          <Route path="/*" element={<UserLayout />} />
        ) : (
          <Route path="/*" element={<DashboardLayout />} />
        )}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </HashRouter>
  );
}

export default App;